import Pubnub, { PubNubConfiguration, PAM, Channel } from 'pubnub';
import { RealtimeChannel } from './channels.js';
import { minutesInMs } from '@gains-com/shared';
import Message = Pubnub.Subscription.Message;

export type RealtimeSubscription = Pubnub.Subscription;
export type RealtimeAuthToken = PAM.Token;
export type RealtimeStatus = Pubnub.Status | Pubnub.StatusEvent;

export interface IRealtimeLogger {
  debug: (message: string, args?: any) => void;
  info: (message: string, args?: any) => void;
  warn: (message: string, args?: any) => void;
  error: (message: string, args?: any) => void;
}

export interface IRealtimeClient<PUB_EVENTS, SUB_EVENTS> {
  readonly pubnub: Pubnub;

  readonly ready: boolean;

  initialize: (config: RealtimeClientConfig) => Promise<void>;

  refreshAuthKey: () => Promise<void>;

  disconnect: () => void;

  reconnect: () => void;

  createSubscription: <EVENT_NAME extends keyof SUB_EVENTS>(
    c: RealtimeChannel,
    subscriptionEventConfigs: { [K in EVENT_NAME]: (arg: SUB_EVENTS[K]) => void | Promise<void> },
  ) => Promise<{ channel: Channel; subscription: RealtimeSubscription; onMessageListener: any }>;

  publishMessage: <EVENT_NAME extends keyof PUB_EVENTS>(
    channel: RealtimeChannel,
    event: EVENT_NAME,
    payload: PUB_EVENTS[EVENT_NAME],
  ) => Promise<void>;
}

export interface RealtimeClientConfig extends PubNubConfiguration {
  /**
   * Provided token refresh function to extend grants for auth key.
   */
  refreshAuthKeyHandler?: () => Promise<{ token: string; uid: string }>;

  /**
   * Will be invoked whenever the auth key is refreshed. Useful for syncing cache of auth tokens.
   */
  onAuthKeyRefresh?: (token: string) => void;

  /**
   * If provided, status events will be listened to and invoked.
   */
  statusHandler?: (status: RealtimeStatus) => void | Promise<void>;

  /**
   * How often to check for auth token expiration. Defaults to 1 minute.
   */
  authHeartbeatIntervalMinutes?: number;

  /**
   * If set, will log additional debug logs.
   */
  debug?: boolean;
}

/**
 * @classDesc Wrapper over the Pubnub library. Adds convenient type support for the GamerGains ecosystem and handles some things like auth for convenience.
 */
export class RealtimeClient<PUB_EVENTS, SUB_EVENTS> implements IRealtimeClient<PUB_EVENTS, SUB_EVENTS> {
  private _pubnub: Pubnub;

  private _config: RealtimeClientConfig;
  private _tokenParsed?: PAM.Token | null = null;

  private _readyTimeoutId: any;

  private _ready: boolean;
  private _readyResolve: (value?: unknown) => void;
  private _readyReject: (reason?: unknown) => void;
  private _readyPromise = new Promise((resolve, reject) => {
    this._readyResolve = resolve;
    this._readyReject = reject;
  });

  private _authHeartbeatInterval: any;

  /**
   * Get the internal pubnub client. Should avoid using if possible.
   */
  get pubnub() {
    if (!this._pubnub) {
      this._logger.warn('Attempted to use realtime client pubnub instance before it was initialized.');
      return null as any; // Hacking as any since it's a bit inconvenient to handle all the improbable null cases
    }

    return this._pubnub;
  }

  private _token: string;

  get tokenParsed() {
    if (!this._tokenParsed) {
      this._tokenParsed = this._pubnub.parseToken(this._token);
    }

    return this._tokenParsed;
  }

  get ready() {
    return this._ready;
  }

  get readyP() {
    return this._readyPromise;
  }

  constructor(private readonly _logger: IRealtimeLogger) {
    this._readyTimeoutId = setTimeout(() => {
      if (!this._ready) {
        this._readyReject('Realtime client was not initialized properly.');
      }
    }, 30 * 1000);
  }

  async initialize(config: RealtimeClientConfig) {
    if (this._config) {
      this._logger.warn('Attempted to initialize realtime client multiple times.');
      return;
    }

    this._logger.info('Initializing realtime client', {
      ...config,
      ...(config.secretKey ? { secretKey: '*** REDACTED ***' } : {}),
      ...(config.authKey ? { authKey: '*** REDACTED ***' } : {}),
    });

    this._config = config;

    if (!config.secretKey) {
      if (!config.refreshAuthKeyHandler) {
        throw Error('REFRESH_AUTH_KEY_HANDLER_REQUIRED');
      }

      // Initialize the auth context if none is already provided.
      if (!config.authKey || !config.userId) {
        this._debugLog('No auth key was provided, fetching new auth key.');
        // This should match refreshAuthKey() logic as best as we can (without the logic pertaining to pubnub sdk)
        const { token, uid } = await config.refreshAuthKeyHandler();
        this._config?.onAuthKeyRefresh?.(token);
        this._token = token;

        this._registerAuthHeartbeatInterval();

        // Set these as part of the config before initialization
        config.authKey = token;
        config.userId = uid;
      } else {
        this._debugLog('Using provided auth key.');
        this._token = config.authKey;
      }
    }

    this._pubnub = new Pubnub(config);

    if (!config.secretKey) {
      // If the provided auth key is a credential that doesn't match the config user id, reset it.
      const tokenAndConfigUserIdMismatch = this.tokenParsed?.authorized_uuid !== config.userId;
      if (tokenAndConfigUserIdMismatch) {
        this._debugLog('Provided token and userId credentials were mismatched', {
          config_user_id: config.userId,
          token_user_id: this.tokenParsed?.authorized_uuid,
        });
        await this.refreshAuthKey();
      } else if (this._isSetTokenExpired()) {
        this._debugLog('Provided token expired. Resetting.', {
          config_user_id: config.userId,
        });
        await this.refreshAuthKey();
      }
    }

    if (this._config.statusHandler) {
      this._debugLog('Realtime status handler provided, listening to status events.');

      // Add a status listener on the pubnub client
      this._pubnub.addListener({ status: this._config.statusHandler });
    }

    this._setClientAsReady();
  }

  reconnect(): void {
    this._pubnub.reconnect();
  }

  disconnect(): void {
    this._pubnub.disconnect();
  }

  async createSubscription<EVENT_NAME extends keyof SUB_EVENTS>(
    c: RealtimeChannel,
    subscriptionEventConfigs: { [K in EVENT_NAME]: (arg: SUB_EVENTS[K]) => void | Promise<void> },
  ) {
    await this._readyPromise;

    const channel = this._pubnub.channel(c);
    const subscription = channel.subscription();

    const onMessageListener = (messageEvent: Message) => {
      // Deployed clients receive events added after they were built — drop
      // unregistered customMessageTypes instead of throwing in PubNub's
      // onMessage path.
      const eventHandler = subscriptionEventConfigs[messageEvent.customMessageType as EVENT_NAME] as
        | ((arg: SUB_EVENTS[EVENT_NAME]) => void | Promise<void>)
        | undefined;

      if (!eventHandler) {
        this._debugLog('Ignoring realtime message with unregistered customMessageType', {
          channel: c,
          custom_message_type: messageEvent.customMessageType,
        });
        return;
      }

      eventHandler(messageEvent.message as SUB_EVENTS[EVENT_NAME]);
    };

    subscription.onMessage = onMessageListener;

    return { channel, subscription, onMessageListener };
  }

  async publishMessage<EVENT_NAME extends keyof PUB_EVENTS>(
    channel: RealtimeChannel,
    event: EVENT_NAME,
    payload: PUB_EVENTS[EVENT_NAME],
    config?: { passthroughError?: boolean; doNotRetryOnAuthFailure?: boolean } & Omit<
      Partial<Pubnub.Publish.PublishParameters>,
      'message' | 'channel' | 'customMessageType'
    >,
  ) {
    await this._readyPromise;

    try {
      await this._pubnub.publish({
        channel,
        customMessageType: event as string,
        message: payload ?? {},
        ...(config ?? {}),
      });
    } catch (err: any) {
      this._logger.error('Realtime publish failed', { channel, event: String(event), error: err });

      // Auto retry is off or in server environment, throw error or bail
      if (config?.doNotRetryOnAuthFailure) {
        if (config?.passthroughError) {
          throw err;
        } else {
          return;
        }
      }

      if (!this._config.secretKey) {
        // If there is no refreshAuthKeyHandler or if it's not a connection error, then throw the error if passthrough is set since retrying won't help
        if (!this._config.refreshAuthKeyHandler || err.status?.category !== 'PNConnectionErrorCategory') {
          if (config?.passthroughError) {
            throw err;
          }
        }

        await this.refreshAuthKey();
      } else {
        // Server environment: bail by default, but let callers opt in to
        // error propagation via `passthroughError` so Promise.allSettled in
        // bulk fan-out can actually count failures.
        if (config?.passthroughError) {
          throw err;
        }
        return;
      }

      try {
        await this._pubnub.publish({ channel, customMessageType: event as string, message: payload ?? {} });
      } catch (retryErr: any) {
        this._logger.error('Retrying publish message failed', { channel, event: String(event), error: retryErr });

        if (config?.passthroughError) {
          throw retryErr;
        }
      }
    }
  }

  /**
   * Refreshes the realtime sdk auth context. If a token is provided, sets that as the token sdk.
   */
  async refreshAuthKey() {
    if (this._config.secretKey) {
      return;
    }

    if (!this._config.refreshAuthKeyHandler) {
      throw Error('ATTEMPTED_REFRESH_AUTH_KEY_WITH_NO_HANDLER');
    }

    this._debugLog(`Refreshing auth key.`);

    const token = (await this._config.refreshAuthKeyHandler()).token;
    this._token = token;
    this._tokenParsed = this._pubnub.parseToken(token);

    if (!this._tokenParsed) {
      throw Error('UNABLED_TO_PARSE_REALTIME_TOKEN');
    }

    this._config.userId = this._tokenParsed.authorized_uuid!;
    this._pubnub.setUserId(this._tokenParsed.authorized_uuid!);
    this._pubnub.setAuthKey(token);
    this._pubnub.setToken(token);
    this._pubnub.reconnect();

    this._config?.onAuthKeyRefresh?.(token);

    this._registerAuthHeartbeatInterval();

    this._debugLog(`Token was successfully refreshed.`);
  }

  /**
   * Marks the realtime client as ready to use. This will allow publishes and subscriptions to go through.
   */
  private _setClientAsReady() {
    this._readyResolve();
    this._ready = true;
    clearTimeout(this._readyTimeoutId);
  }

  private _registerAuthHeartbeatInterval() {
    if (this._config.secretKey) {
      return;
    }

    if (this._authHeartbeatInterval) {
      clearInterval(this._authHeartbeatInterval);
    }

    // Make sure to check immediately as well as every interval;
    this._checkAutoRefreshToken();
    this._authHeartbeatInterval = setInterval(
      () => this._checkAutoRefreshToken(),
      minutesInMs(this._config.authHeartbeatIntervalMinutes ?? 1),
    );
  }

  private _checkAutoRefreshToken() {
    if (this._config.secretKey) {
      return;
    }

    if (this._isSetTokenExpired()) {
      this._debugLog(`Token expired.`);

      this.refreshAuthKey().then();
    } else {
      this._debugLog(`Token still valid.`);
    }
  }

  private _debugLog(message: string, attributes?: any) {
    if (this._config.debug) {
      this._logger.debug(message, attributes);
    }
  }

  private _errorLog(message: string, attributes?: any) {
    this._logger.error(message, attributes);
  }

  private _isSetTokenExpired() {
    if (!this._tokenParsed) {
      return true;
    }

    const ttlDurationInMS = minutesInMs(this._tokenParsed.ttl);
    // We add a 5 minute buffer to avoid edge cases
    const ttlInMS = this._tokenParsed.timestamp * 1000 + ttlDurationInMS - minutesInMs(5);

    return Date.now() > ttlInMS;
  }
}
